// The human ground truth the judge is graded against.
//
// THE DIRECTION MATTERS: the human verdicts are ground truth FOR THE JUDGE, not
// the reverse. This is the client's own P0 QA run — 16 cases, scored by hand by
// their tester, with a written reason on every one. It is transcribed verbatim
// from BRD/tablestake.md via eval/suites/tablestake_p0.jsonl, where the `partner`
// field carries that run's verdict and `provenance.checker_verdict` carries the
// tester's note. Neither is ever edited to agree with a later build: it is a
// dated observation, not an opinion.
//
// It lives here, in the console, because it is the reference the Calibration
// view joins the judge's verdicts against. It is 16 rows and it does not change.

/** pass / partial / fail — the three levels the tester actually used. */
export type HumanVerdict = "pass" | "partial" | "fail";

export interface HumanCase {
  case_id: string;
  scenario: string;
  lang: string;
  human: HumanVerdict;
  /** The tester's written reason. Empty when the sheet's notes cell was blank. */
  reason: string;
}

export const TABLESTAKE_P0: HumanCase[] = [
  {
    case_id: "TS-01-sim-swap-live-hing",
    scenario: "sim-swap-live",
    lang: "hing",
    human: "partial",
    reason: "1. Identified it as a SIM swap and informed the user to contact the telecom provider, but when asked for the telecom provider number, said it cannot share the number. 2. Changes language from Hinglish to English in the middle of the conversation. 3. Loses the context completely and starts repeating the same questions.",
  },
  {
    case_id: "TS-02-malicious-app-hing",
    scenario: "malicious-app-containment",
    lang: "hing",
    human: "fail",
    reason: "1. Changes language from Hinglish to English. 2. Unable to identify the scam type clearly and assumed it was a SIM swap. When asked to recheck, it informed the user that it was a malicious app and was eventually able to guide the right steps. It then again lost track of the conversation and repeatedly asked the user to confirm whether the steps had been completed, despite confirmation.",
  },
  {
    case_id: "TS-03-ransomware-business-hing",
    scenario: "ransomware-business",
    lang: "hing",
    human: "fail",
    reason: "1. Did not identify it as an active ongoing scam at all. 2. Changes language from Hinglish to English. 3. Completely misidentified the scam and treated it as a SIM-swap scam.",
  },
  {
    case_id: "TS-04-vendor-payment-diversion-hing",
    scenario: "vendor-payment-diversion",
    lang: "hing",
    human: "partial",
    reason: "Not able to understand the concern when explained in Hinglish, but gave the correct response when the same was asked in English.",
  },
  {
    case_id: "TS-05-claim-intake-hing",
    scenario: "claim-intake",
    lang: "hing",
    human: "fail",
    reason: "1. Identified that it was a claim request. 2. Got stuck after the first step. Despite being informed several times that the steps had been completed and it should carry on, it asked the same questions again.",
  },
  {
    case_id: "TS-06-claim-documents-hing",
    scenario: "claim-documents",
    lang: "hing",
    human: "fail",
    reason: "1. Runs in loops asking the user to complete steps that are unrelated to an insurance claim. 2. Despite being informed several times, it was not able to identify that the user wanted to make a cyber-insurance claim.",
  },
  {
    case_id: "TS-07-claim-rejection-appeal-hing",
    scenario: "claim-rejection-appeal",
    lang: "hing",
    human: "pass",
    reason: "Explained it clearly.",
  },
  {
    case_id: "TS-08-coverage-hypothetical-en",
    scenario: "coverage-enquiry-hypothetical",
    lang: "en",
    human: "fail",
    reason: "1. Did not understand the question. Even after it was rephrased, it still assumed that the user had already been scammed.",
  },
  {
    case_id: "TS-09-phone-theft-boundary-hing",
    scenario: "coverage-boundary-theft",
    lang: "hing",
    human: "fail",
    reason: "1. Said it had no idea whether phone theft was covered by insurance and requested the user to call the insurer or 1930 to check.",
  },
  {
    case_id: "TS-10-devanagari-hold-hi",
    scenario: "language-hold-devanagari",
    lang: "hi",
    human: "partial",
    reason: "1. Replied properly in Hindi once, but the conversation did not continue further. Despite further messages and attempts to continue the conversation, it stopped responding.",
  },
  {
    case_id: "TS-11-tamil-hold-ta",
    scenario: "language-hold-regional",
    lang: "ta",
    human: "pass",
    reason: "Replied properly in Tamil and stayed in the same language throughout the conversation.",
  },
  {
    case_id: "TS-12-phonetic-romanized-hing",
    scenario: "phonetic-romanized-input",
    lang: "hing",
    human: "pass",
    reason: "Identified the issue correctly and responded accordingly.",
  },
  {
    case_id: "TS-13-context-carry-icici-hing",
    scenario: "context-carry-multiturn",
    lang: "hing",
    human: "pass",
    reason: "Continued the conversation and remembered the issue. Guided the user that it did not have the ICICI Bank helpline number, explained where the number could be found, and what to say and collect.",
  },
  {
    case_id: "TS-14-human-handoff-en",
    scenario: "human-handoff-request",
    lang: "en",
    human: "pass",
    reason: "(the tester left the notes cell empty)",
  },
  {
    case_id: "TS-15-large-loss-escalation-en",
    scenario: "large-loss-escalation",
    lang: "en",
    human: "partial",
    reason: "The bot took some time to understand the concern initially and kept asking generic questions, such as whether it was happening to the user or their family. It did not directly say it was fraud and jumped into carrying out the steps. When confronted, it apologised, confirmed that it was fraud, and guided the user through the correct steps.",
  },
  {
    case_id: "TS-16-after-hours-availability-en",
    scenario: "after-hours-availability",
    lang: "en",
    human: "fail",
    reason: "1. It was not clear whether an agent was available immediately. It only provided a generic message that an agent would reach out and did not provide any actions to carry out in the meantime.",
  },
];

/** The sheet's own headline, counted from the rows above rather than restated. */
export function tablestakeCounts(): Record<HumanVerdict, number> {
  const out: Record<HumanVerdict, number> = { pass: 0, partial: 0, fail: 0 };
  for (const row of TABLESTAKE_P0) out[row.human] += 1;
  return out;
}
